import { ColumnEditorOptions } from "primereact/column";
import { Question } from "../../types/checklist";
import {
    BooleanEditor,
    booleanDisplayTemplate,
    SingleChoiceEditor,
    singleChoiceDisplayTemplate,
    MultiChoiceEditor,
    multiChoiceDisplayTemplate,
    NumberEditor,
    numberDisplayTemplate,
    DateEditor,
    dateDisplayTemplate,
    TextEditor,
} from "./editors";

interface AnswerEditorProps {
    options: ColumnEditorOptions;
    question?: Question;
    fallbackType?: string | null;
    // Discrete types (boolean/single-choice) save on selection via this rather
    // than waiting for the cell to blur so the cascading questions appear instantly
    onCommit?: (value: unknown) => void;
}

/**
 * Dynamic answer editor that renders the appropriate input control
 * based on the question type from the checklist configuration.
 * Falls back to using the answer's own questionType when checklist is unavailable.
 */
export const AnswerEditor = ({ options, question, fallbackType, onCommit }: AnswerEditorProps) => {
    const questionType = question?.type || fallbackType;

    // If no question type available at all, fall back to text editor
    if (!questionType) {
        return <TextEditor options={options} />;
    }

    switch (questionType) {
        case "boolean":
            return <BooleanEditor options={options} onCommit={onCommit} />;

        case "single-choice":
            if (!question?.options || question.options.length === 0) {
                return <TextEditor options={options} />;
            }
            return (
                <SingleChoiceEditor
                    options={options}
                    questionOptions={question.options}
                    onCommit={onCommit}
                />
            );

        case "multi-choice":
            if (!question?.options || question.options.length === 0) {
                return <TextEditor options={options} />;
            }
            return (
                <MultiChoiceEditor
                    options={options}
                    questionOptions={question.options}
                />
            );

        case "number":
            return (
                <NumberEditor
                    options={options}
                    unit={question?.unit}
                    min={question?.validation?.min}
                    max={question?.validation?.max}
                />
            );

        case "date":
            return <DateEditor options={options} />;

        case "text":
            return (
                <TextEditor
                    options={options}
                    maxLength={question?.validation?.maxLength}
                />
            );

        case "table":
            // Table type is complex, fall back to text for now
            return <TextEditor options={options} />;

        default:
            return <TextEditor options={options} />;
    }
};

/**
 * Get the display value for an answer based on question type.
 * Converts internal values (option IDs, booleans) to human-readable text.
 */
export const getAnswerDisplayValue = (value: unknown, question?: Question): string => {
    if (value === null || value === undefined) return "";

    if (!question) {
        return String(value);
    }

    switch (question.type) {
        case "boolean":
            return booleanDisplayTemplate(value);

        case "single-choice":
            return singleChoiceDisplayTemplate(value, question.options);

        case "multi-choice":
            return multiChoiceDisplayTemplate(value, question.options);

        case "number":
            return numberDisplayTemplate(value, question.unit);

        case "date":
            return dateDisplayTemplate(value);

        default:
            return String(value);
    }
};
